import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EmptyCard from '../../../components/common/Cards/emptyCard';
import EnhancedTable from '../../../components/common/Table';
import empty from '../../../assets/images/empty.png';
import { ROLES } from '../../../utils/constants';
import ProjectApi from '../project-api';
import StatusIndicator from '../../../components/common/StatusIndicator';
import { STATUS_CLASSNAMES } from '../../ClientContainer/constants';
import Alert from '../../../components/common/Alert';
import Menu, { MenuContainer } from '../../../components/common/Menu';
import { useHistory } from 'react-router-dom';
import UsersAPI from '../../UserContainer/user-api';
import Button from '../../../components/common/Button';
import ConfirmationModal from '../../ClientContainer/statusModal';
import useDebounce from '../../../hooks/useDebounce';

const ACTIONS = {
  INACTIVE: 'inactive',
  DELETED: 'delete',
  REJECT: 'reject',
  ACTIVATE: 'activate',
};
const LIMIT_PER_PAGE = 25;

const MentorsTable = ({ project }) => {
  const history = useHistory();
  const [mentors, setMentors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientId, setClientId] = useState();
  const [isMenuOpen, setIsMenuOpen] = useState({ open: false, id: '' });
  const menuRef = React.useRef(null);
  const [isModalOpen, setIsModalOpen] = useState({
    open: false,
    message: '',
    titulo: '',
  });
  const [mentorSelected, setMentorSelected] = useState();
  const [usersSelected, setUsersSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);

  const loadMentors = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: {
          mentors: fetchedMentors = [],
          clientId: fetchedClientId,
          totalPages: fetchedTotalPages = 0,
          pageNumber = 1,
        },
      } = await ProjectApi.fetchUserByProject(
        project.announcementId,
        ROLES.Mentor,
        currentPage,
        LIMIT_PER_PAGE,
        debouncedSearchText
      );

      setClientId(fetchedClientId);
      setMentors(fetchedMentors);
      setTotalPages(fetchedTotalPages);

      if (pageNumber !== currentPage) {
        setCurrentPage(pageNumber);
      }
    } catch (fetchError) {
      setError(fetchError?.data?.message || 'No se pudo obtener el listado de mentores');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchText, project.announcementId]);

  useEffect(() => {
    loadMentors();
  }, [loadMentors]);

  const onClick = (id) => {
    setIsMenuOpen((prev) => ({ id, open: !prev.open }));
  };

  useEffect(() => {
    if (!isMenuOpen.open) return;
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen({ open: false, id: '' });
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen.open]);

  const handleActivation = async ({ mentorId, status }) => {
    try {
      await UsersAPI.updateUser(mentorId, {
        status,
      });
      await loadMentors();
    } catch (updateError) {
      setError(updateError?.data?.message || 'No se pudo actualizar el mentor');
    } finally {
      setIsModalOpen({ open: false, message: '', titulo: '' });
    }
  };

  const onAccept = (action, mentorId) => {
    setIsMenuOpen({ open: false, id: '' });
    switch (action) {
      case ACTIONS.INACTIVE:
        handleActivation({ mentorId, status: 'deactivated' });
        break;
      case ACTIONS.REJECT:
        handleActivation({ mentorId, status: 'rejected' });
        break;
      case ACTIONS.ACTIVATE:
        handleActivation({ mentorId, status: 'active' });
        break;
      default:
        break;
    }
  };

  const handleChangeOfStatus = (action, mentorId) => {
    switch (action) {
      case ACTIONS.INACTIVE:
        setMentorSelected({ mentor: mentorId, action: ACTIONS.INACTIVE });
        setIsModalOpen({
          open: true,
          message:
            'el usuario se inactivará, no podrá acceder a la plataforma o asistir a reuniones. ¿Estás seguro de inactivarlo?',
          titulo: 'Inactivar mentor',
        });
        break;
      case ACTIONS.REJECT:
        setMentorSelected({ mentor: mentorId, action: ACTIONS.REJECT });
        setIsModalOpen({
          open: true,
          message:
            'El mentor será marcado como rechazado. ¿Estás seguro de rechazarlo?',
          titulo: 'Rechazar mentor',
        });
        break;
      case ACTIONS.ACTIVATE:
        setMentorSelected({ mentor: mentorId, action: ACTIONS.ACTIVATE });
        setIsModalOpen({
          open: true,
          message: '¿Estás seguro de activar al mentor?',
          titulo: 'Activar mentor',
        });
        break;
      default:
        break;
    }
  };

  const renderStatus = (status) => {
    if (status === 'Rechazado') {
      return (
        <StatusIndicator
          text="Mentor no aprobado"
          statusClassName={STATUS_CLASSNAMES.REJECTED}
          error
        />
      );
    }

    if (status === 'deactivated') {
      return (
        <StatusIndicator
          text="Desactivado"
          statusClassName={STATUS_CLASSNAMES.INACTIVE}
        />
      );
    }

    const className =
      status === 'Activo'
        ? STATUS_CLASSNAMES.ACTIVE
        : STATUS_CLASSNAMES.INACTIVE;

    return <StatusIndicator text={status} statusClassName={className} />;
  };

  const renderSignupState = (step) => {
    if (step === 0) {
      return 'Registro completo';
    } else if (step === 1) {
      return 'Registro Inicial';
    } else {
      return 'Registro Incompleto';
    }
  };

  const onClickApplication = async (isAccepted, users = []) => {
    try {
      await ProjectApi.updateUserApplication(project.announcementId, {
        isAccepted,
        users,
      });
      setUsersSelected([]);
      await loadMentors();
    } catch (requestError) {
      console.log(requestError);
    }
  };

  const detachUser = async (userId) => {
    try {
      await ProjectApi.deleteUserAnnouncement(project.announcementId, userId);
      await loadMentors();
    } catch (requestError) {
      console.log(requestError);
    }
  };

  const pdfDownload = async (id) => {
    try {
      const { data } = await UsersAPI.fetchPdfUser(id);
      window.open(data.link, '_blank');
    } catch (downloadError) {
      console.error('Error fetching PDF data:', downloadError.message);
      setError('Ocurrio un error al descargar el PDF');
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'Nombre', accessor: 'name' },
      { Header: 'E-mail', accessor: 'email' },
      {
        Header: 'Estado',
        accessor: 'status',
        Cell: ({ value }) => renderStatus(value),
      },
      {
        Header: 'Estado Registro',
        Cell: ({ row: { original } }) => renderSignupState(original.registro),
      },
      {
        Header: 'Estado postulacion',
        accessor: 'isAccepted',
      },
      {
        Header: 'Pilar',
        accessor: 'pillar',
      },
      {
        Header: 'Acción',
        accessor: 'id',
        Cell: ({ value, row: { original } }) => (
          <MenuContainer onClick={() => onClick(value)}>
            {isMenuOpen.id == value && (
              <div ref={menuRef}>
                <Menu
                  open={isMenuOpen.open}
                  items={[
                    {
                      text: 'Editar',
                      action: () =>
                        history.push('/usuarios/editar', {
                          idUser: value,
                          roleType: ROLES.Mentor,
                        }),
                    },
                    {
                      text: 'Aceptar',
                      action: () => {
                        onClickApplication(true, [original.id]);
                      },
                      status:
                        original.isAccepted === 'Aceptado'
                          ? 'disabled'
                          : 'featured',
                    },
                    {
                      text: 'Rechazar Postulación',
                      action: () => onClickApplication(false, [original.id]),
                      status:
                        original.isAccepted === 'Rechazado'
                          ? 'disabled'
                          : 'warning',
                    },
                    {
                      text: 'Liberar de convocatoría',
                      action: () => detachUser(original.id),
                      status: 'warning',
                    },
                    {
                      text:
                        original.status === 'Activo' ? 'Desactivar' : 'Activar',
                      action: () =>
                        original.status === 'Activo'
                          ? handleChangeOfStatus(ACTIONS.INACTIVE, value)
                          : handleChangeOfStatus(ACTIONS.ACTIVATE, value),
                    },
                    {
                      text: 'Rechazar',
                      action: () => handleChangeOfStatus(ACTIONS.REJECT, value),
                      status: 'warning',
                    },
                    {
                      text: 'Descargar PDF',
                      action: () => pdfDownload(original.id),
                    },
                  ]}
                />
              </div>
            )}
          </MenuContainer>
        ),
      },
    ],
    [history, isMenuOpen]
  );

  return (
    <div>
      {error && (
        <div>
          <Alert type="error">{error}</Alert>
        </div>
      )}
      {isLoading || mentors.length ? (
        <EnhancedTable
          data={mentors}
          columns={columns}
          isLoadingData={isLoading}
          tableName="Mentores"
          buttonLabel="Nuevo Participante"
          onClickButton={() => {
            history.push('/clientes/detalles/proyecto/agregar', {
              client: {
                id: clientId,
                name: project.client,
                status: project.clientStatus,
              },
              announcement: {
                id: project.announcementId,
                title: project.title
              },
              role: 'mentor',
            });
          }}
          onChangePage={(event, newPage) => setCurrentPage(newPage)}
          currentPage={currentPage}
          totalPages={totalPages}
          onSearch={(event) => {
            setCurrentPage(1);
            setSearchText(event.target.value || '');
          }}
          isDynamic
        />
      ) : (
        <div className="empty-tab">
          <EmptyCard
            message="Actualmente no existen mentores registrados"
            img={empty}
          >
            <Button
              className="actions-table__button button"
              type="button"
              onClick={() => {
                history.push('/clientes/detalles/proyecto/agregar', {
                  client: {
                    id: clientId,
                    name: project.client,
                    status: project.clientStatus,
                  },
                  announcement: {
                    id: project.announcementId,
                    title: project.title
                  },
                  role: 'mentor',
                });
              }}
            >
              <span className="material-symbols-outlined">add</span>
              Crear nuevo usuario
            </Button>
          </EmptyCard>
        </div>
      )}
      <ConfirmationModal
        title={isModalOpen?.message}
        open={isModalOpen.open}
        header={isModalOpen?.titulo}
        handleDenied={() =>
          setIsModalOpen({ open: false, message: '', titulo: '' })
        }
        handleAccept={() => {
          onAccept(mentorSelected.action, mentorSelected.mentor);
        }}
        onClose={() => setIsModalOpen({ open: false, message: '', titulo: '' })}
      />
    </div>
  );
};

export default MentorsTable;
